import { OfficeRequestStatus } from "../../modules/schedule/types/schedule.types";
import { RecurrenceRule } from "../types";
import { TaskCategory } from "../types/tasks";

/**
 * Setting Default practice ids for mantle and humantold
 * We should use this in parts of the app where we need to make sure adding new practices
 * does not break what we currently have (e.g pinned clients flow). Locally and on staging the default is mantle's practice id
 * but on prod we default to humantold practice id
 */
export const DEFAULT_PRACTICE_ID =
    import.meta.env.VITE_NODE_ENV === "production"
        ? "501ae593-0c7b-4c7a-89d9-65f4d5e09243"
        : "51f9c194-cc32-48ad-8794-2962ceed8f4b";

export const APP_COLORS = {
    COLOR_PRIMARY: "#1a738d",
    COLOR_PRIMARY_DARK: "#06615d",
    COLOR_SECONDARY: "#e3eaed",
    COLOR_SECONDARY_ALT: "#d6d5c9",
    COLOR_SECONDARY_LIGHT: "#f8f8f6",
    COLOR_TERTIARY: "#4b4e51",
    COLOR_BLACK: "#0b132b",
    COLOR_WHITE: "#ffffff",
    COLOR_GRAY: "#576a81",
    COLOR_DANGER: "#f71735",
    COLOR_WARNING: "#fcb400",
    GREEN_900: "#0E5730",
    GREEN_300: "#BAECD1",
    GRAY_300: "#D3D5D9",
    GRAY_900: "#33363B",
};

export const UNIQUE_CLIENT_EMAIL_REGEX = /^\d+_{2,}/;
export const UNIQUE_CLIENT_EMAIL_SEPARATOR = "__";

/** Search param used to carry the practice a link belongs to into a new tab */
export const PRACTICE_ID_SEARCH_PARAM = "practice_id";

export const LISTS_DEFAULT_LIMIT = 100;

export const COMMUNICATION_HISTORY_DATE_FORMAT = "MM/dd/yyyy h:mm:ss a";

export const LISTS_DEFAULT_PAGE = 1;

export const CPT_CODES_PSYCH = [
    "96116",
    "96132",
    "96133",
    "96136",
    "96137",
    "96138",
    "96139",
];

export const CPT_CODES_PSYCHO = [
    "90847",
    "99449",
    "90837",
    "90834",
    "90846",
    "90832",
    "90791",
    "99448",
    "90785",
    "no_show",
    "ROD",
];

export const CPT_CODES = [
    "90847",
    "99449",
    "90837",
    "90834",
    "90846",
    "90832",
    "90791",
    "99448",
    "90785",
    "no_show",
    "ROD",
    "96116",
    "96136",
    "96137",
    "96138",
    "96139",
    "96132",
    "96133",
];

interface ICptCodesDescription {
    [key: string]: string;
}

export const CPT_CODES_DESCRIPTION: ICptCodesDescription = {
    "99203": "New patient office/outpatient E/M, level 3",
    "99204": "New patient office/outpatient E/M, level 4",
    "99205": "New patient office/outpatient E/M, level 5",
    "99212": "Established patient office/outpatient E/M, level 2",
    "99213": "Established patient office/outpatient E/M, level 3",
    "99214": "Established patient office/outpatient E/M, level 4",
    "99215": "Established patient office/outpatient E/M, level 5",
    "90833": "Psychotherapy add-on, 30 minutes",
    "90836": "Psychotherapy add-on, 45 minutes",
    "90838": "Psychotherapy add-on, 60 minutes",
    "90837": "Psychotherapy, 60 minutes (Individual psychotherapy)",
    "90834": " Psychotherapy, 45 minutes (Individual psychotherapy)",
    "90832": "Psychotherapy, 30 minutes (Individual psychotherapy)",
    "90846": "Family psychotherapy without the patient present",
    "90847": "Family psychotherapy with the patient present",
    "90791": "Psychiatric diagnostic evaluation (Initial evaluation)",
    "99449": "Interprofessional consult: 11-20 min telehealth consult",
    "99448": "Interprofessional consult: 5-10 min telehealth consult",
    "90785": "Interactive complexity (add-on to primary service)",
    no_show: "No Show",
    ROD: " INTERPROFESSIONAL CONSULT: TELEHEALTH CONSULT",
    "96116": "Neurobehavioral status exam, 1st hour",
    "96136":
        "Psych/neuropsych test admin & scoring by professional, 1st 30 min",
    "96137":
        "Psych/neuropsych test admin & scoring by professional, additional 30 min",
    "96138": "Psych/neuropsych test admin & scoring by technician, 1st 30 min",
    "96139":
        "Psych/neuropsych test admin & scoring by technician, additional 30 min",
    "96132": "Neuropsych testing evaluation, 1st hour",
    "96133": "Neuropsych testing evaluation, additional hour",
};

export const RECURRENCE_COLORS: { [key: string]: string } = {
    [RecurrenceRule.WEEKLY.toLowerCase()]: "#5F9EA0",
    [RecurrenceRule.BIWEEKLY.toLowerCase()]: "#B8860B",
    "2x/weekly": "#93AACC",
    [RecurrenceRule.TRIWEEKLY.toLowerCase()]: "#F08080",
    [RecurrenceRule.MONTHLY.toLowerCase()]: "#708090",
    [RecurrenceRule.ONE_OFF.toLowerCase()]: "#198988",
    [RecurrenceRule.TWO_MONTHS.toLowerCase()]: "#2867b5",
    [RecurrenceRule.THREE_MONTHS.toLowerCase()]: "#39209e",
};

export const OFFICE_REQUEST_STATUS_COLORS = {
    [OfficeRequestStatus.APPROVED]: "#33cc33",
    [OfficeRequestStatus.REJECTED]: "#cc0000",
    [OfficeRequestStatus.PENDING]: "#669999",
    [OfficeRequestStatus.APPROVED_WITH_CHANGES]: "#708090",
};

export const getColorStylesForCalendarId = (recurrenceRule: string) => {
    switch (recurrenceRule) {
        case RecurrenceRule.WEEKLY:
            return {
                backgroundColor:
                    "linear-gradient(246deg, rgba(172, 222, 250, 0.43) -2.89%, rgba(172, 222, 250, 0.35) 36.64%, rgba(172, 222, 250, 0.20) 64.37%, rgba(172, 222, 250, 0.00) 101.31%)",
                tagBgColor: "#ACDEFA",
                textColor: "#165574",
                color: "#0F96DA",
            };
        case RecurrenceRule.BIWEEKLY:
            return {
                backgroundColor:
                    "linear-gradient(246deg, rgba(199, 250, 167, 0.43) -2.89%, rgba(199, 250, 167, 0.35) 36.64%, rgba(199, 250, 167, 0.20) 73.18%, rgba(199, 250, 167, 0.00) 101.31%)",
                tagBgColor: "#C0F3A1",
                textColor: "#2F6217",
                color: "#6DA951",
            };
        case RecurrenceRule.MONTHLY:
            return {
                backgroundColor:
                    "linear-gradient(246deg, rgba(252, 198, 234, 0.47) -2.89%, rgba(252, 198, 234, 0.35) 36.64%, rgba(252, 180, 228, 0.20) 73.18%, rgba(252, 180, 228, 0.00) 101.31%)",
                tagBgColor: "#F9A4DD",
                textColor: "#62174C",
                color: "#D34CAB",
            };
        case RecurrenceRule.ONE_OFF:
            return {
                backgroundColor:
                    "linear-gradient(246deg, rgba(230, 230, 250, 0.74) -2.89%, rgba(230, 230, 250, 0.74) 36.64%, rgba(230, 230, 250, 0.20) 81.29%, rgba(230, 230, 250, 0.00) 101.31%)",
                tagBgColor: "#D1D5F8",
                textColor: "#2B2B86",
                color: "#7676E6",
            };
        // update background colors when design provides that
        case RecurrenceRule.TRIWEEKLY:
            return {
                backgroundColor: "rgba(35, 209, 96, 0.7)",
                tagBgColor: "#B4B4FA",
                textColor: "#485199",
            };

        case RecurrenceRule.TWO_MONTHS:
            return {
                backgroundColor: "rgba(35, 209, 96, 0.7)",
                tagBgColor: "#D1D5F8",
                textColor: "#485199",
            };
        case RecurrenceRule.THREE_MONTHS:
            return {
                backgroundColor: "rgba(35, 209, 96, 0.7)",
                tagBgColor: "#D1D5F8",
                textColor: "#485199",
            };

        default:
            return {
                backgroundColor:
                    "linear-gradient(246deg, rgba(172, 222, 250, 0.43) -2.89%, rgba(172, 222, 250, 0.35) 36.64%, rgba(172, 222, 250, 0.20) 64.37%, rgba(172, 222, 250, 0.00) 101.31%)",
                tagBgColor: "#ACDEFA",
                textColor: "#165574",
                color: "#0F96DA",
            };
    }
};

/**
 * Note content labels
 * PLEASE DO NOT EDIT UNLESS NECESSARY2
 */

// CANCELLATION NOTES LABELS
export const CANCELLATION_NOTE_LABELS = {
    reason: "Reason Provided By Client for Cancelling",
};

// SUPERVISION NOTES LABELS
export const SUPERVISION_NOTE_LABELS = {
    supervision_note: "Supervision note",
    client_progress: "Client progress",
};

// PAUSE NOTIFICATION NOTE LABELS NOTES LABELS
export const PAUSE_NOTIFICATION_NOTE_LABELS = {
    pause_reason:
        "Please indicate reason client is currently on pause for treatment",
};

export const GENERAL_UPDATE_NOTE_LABELS = {
    update_note_content:
        "Current Client Status (Document updates on the client current state and follow up)",
    date_of_update: "Date of Update",
    diagnosis_codes: "Primary diagnosis",
    secondary_diagnosis: "Secondary diagnosis",
    tertiary_diagnosis: "Tertiary diagnosis",
    additional_diagnosis_one: "Additional Diagnosis 1",
    additional_diagnosis_two: "Additional Diagnosis 2",
};

// TERMINATION NOTES LABELS
export const TERMINATION_NOTE_LABELS = {
    appointment_location: "Appointment location",
    delivery_option: "Client Teletherapy Location",
    other_delivery_option: "Specify other client teletherapy location",
    provider_location: "Provider Teletherapy Location",
    other_provider_location: "Specify other provider teletherapy location",
    consent_was_received_for_the_teletherapy_session:
        "Consent was received for the teletherapy session",
    the_teletherapy_session_was_conducted_via_video:
        "The teletherapy session was conducted via video",
    duration: "Duration",
    first_session_date: "First session date",
    last_session_date: "Last session date",
    total_number_of_sessions: "Total number of sessions",
    diagnosis_codes: "Primary diagnosis",
    secondary_diagnosis: "Secondary diagnosis",
    tertiary_diagnosis: "Tertiary diagnosis",
    additional_diagnosis_one: "Additional Diagnosis 1",
    additional_diagnosis_two: "Additional Diagnosis 2",
    presenting_problems: "Presenting problems",
    planned_treatment_and_goals: "Planned treatment and goals",
    course_of_treatment: "Course of treatment",
    patient_final_condition: "Patient final condition",
    prognosis: "Prognosis",
    reason_for_termination: "Reason for termination",
    discharge_plan_and_follow_up: "Discharge plan and follow-up",
    date_created: "Date created",
};

// TREATMENT REVIEW LABELS
export const TREATMENT_REVIEW_NOTE_LABELS = {
    name: "Name",
    gender: "Gender",
    preferred_name: "Preferred name",
    other_gender: "Other gender",
    pronouns: "Pronouns",
    other_sexual_orientation: "Other sexual orientation",
    religion: "Religion",
    education: "Education",
    race: "Race",
    ethnicity: "Ethnicity",
    sexual_orientation: "Sexual orientation",
    current_employment: "Current employment",
    current_employment_details: "Current employment details",
    relationship_status: "Relationship status",
    name_of_partner: "Name of partner",
    emergency_contact_information: "Emergency contact information",
    legal_problems: "Legal problems",
    isClientPresent: "Is client present",
    appointment_location: "Appointment location",
    delivery_option: "Client Teletherapy Location",
    other_delivery_option: "Specify other client teletherapy location",
    provider_location: "Provider Teletherapy Location",
    other_provider_location: "Specify other provider teletherapy location",
    consent_was_received_for_the_teletherapy_session:
        "Consent was received for the teletherapy session",
    the_teletherapy_session_was_conducted_via_video:
        "The teletherapy session was conducted via video",
    duration: "Duration",
    diagnosis_codes: "Primary diagnosis",
    secondary_diagnosis: "Secondary diagnosis",
    tertiary_diagnosis: "Tertiary diagnosis",
    additional_diagnosis_one: "Additional Diagnosis 1",
    additional_diagnosis_two: "Additional Diagnosis 2",
    what_are_the_problem_you_are_seeking_help_for:
        "What are the problem(s) you are seeking help for?",
    anxiety: "Anxiety",
    anxiety_frequency: "Anxiety frequency",
    anxiety_details: "Anxiety details",
    panic_attacks: "Panic attacks",
    panic_attacks_freqency: "Panic attacks frequency",
    panic_attacks_details: "Panic attacks details",
    depression: "Depression",
    depression_freqency: "Depression frequency",
    depression_details: "Depression details",
    other_symptoms: "Other symptoms",
    physical_symptoms: "Physical symptoms",
    medication_physical_psychiatric: "Medication physical/psychiatric",
    sleeping_habits_and_concerns: "Sleeping habits and concerns",
    difficulties_with_appetite_or_eating_patterns:
        "Difficulties with appetite or eating patterns",
    been_so_distressed_you_seriously_wished_to_end_your_life:
        "Been so distressed you seriously wished to end your life?",
    describe_been_so_distressed_you_seriously_wished_to_end_your_life:
        "Describe the situation",
    has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live:
        "Has anything happened recently to make you feel you don’t want to live?",
    describe_has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live:
        "Describe the situation",
    a_specific_plan_of_how_you_would_kill_yourself:
        "A specific plan of how you would kill yourself?",
    describe_a_specific_plan_of_how_you_would_kill_yourself:
        "Describe the situation",
    access_to_weapons_or_means_of_hurting_self:
        "Access to weapons or means of hurting self?",
    describe_access_to_weapons_or_means_of_hurting_self:
        "Describe the situation",
    made_serious_suicide_attempts: "Made serious suicide attempts?",
    describe_made_serious_suicide_attempts: "Describe the situation",
    purposely_done_something_to_hurt_yourself:
        "Purposely done something to hurt yourself?",
    describe_done_something_to_hurt_yourself: "Describe the situation",
    heard_voices_telling_you_to_hurt_yourself:
        "Heard voices telling you to hurt yourself?",
    describe_heard_voices_telling_you_to_hurt_yourself:
        "Describe the situation",
    had_relatives_who_attempted_or_commited_sucide:
        "Had relatives who attempted or committed suicide?",
    describe_had_relatives_who_attempted_or_commited_sucide:
        "Describe the situation",
    had_thoughts_of_killing_or_seriously_hurting_someone:
        "Had thoughts of killing or seriously hurting someone?",
    describe_had_thoughts_of_killing_or_seriously_hurting_someone:
        "Describe the situation",
    heard_voices_telling_you_to_hurt_others:
        "Heard voices telling you to hurt others?",
    describe_heard_voices_telling_you_to_hurt_others: "Describe the situation",
    hurt_someone_or_destroyed_property_on_purpose:
        "Hurt someone or destroyed property on purpose?",
    describe_hurt_someone_or_destroyed_property_on_purpose:
        "Describe the situation",
    slapped_kicked_punched_someone_with_intent_to_harm:
        "Slapped, kicked, punched someone with intent to harm?",
    describe_slapped_kicked_punched_someone_with_intent_to_harm:
        "Describe the situation",
    been_arrested_or_detained_for_violent_behavior:
        "Been arrested or detained for violent behavior?",
    describe_been_arrested_or_detained_for_violent_behavior:
        "Describe the situation",
    been_to_jail_for_any_reason: "Been to jail for any reason?",
    describe_been_to_jail_for_any_reason: "Describe the situation",
    been_on_probation_for_any_reason: "Been on probation for any reason?",
    describe_been_on_probation_for_any_reason: "Describe the situation",
    do_you_have_access_to_guns: "Do you have access to guns?",
    describe_do_you_have_access_to_guns: "Describe the situation",
    patients_ability_and_capability_to_respond_to_treatment:
        "Patient's ability and capability to respond to treatment",
    details_for_diminished_poor_limited_quality_patient_oriented_evidence:
        "Please provide details for diminished/poor quality patient-orientation",
    people_present_at_session: "Present at session",
    other_person_present_at_session: "Specify other person present",
    type_of_treatment_recommended: "Type of treatment recommended",
    treatment_modality_intervention: "Treatment Modality/Intervention(s)",
    other_treatment_modality_intervention: "Specify Other",
    treatment_goal_one: "1.",
    treatment_goal_two: "2.",
    treatment_goal_three: "3.",
    medication_updates: "Medication updates",
    frequency_of_sessions: "Frequency of sessions",
    other_frequency_of_sessions: "Please specify if other",
    expected_termination_date: "Expected termination date",
    plan_of_care: "Plan of Care",
    prognosis: "Prognosis",
    subjective_report: "Subjective report",
    summary: "Summary",
    selected_symptoms: "Selected Symptoms",
};

// RECORD OF DISCLOSURE LABELS
export const RECORD_OF_DISCLOSURE_NOTE_LABELS = {
    duration: "Duration",
    diagnosis_codes: "Primary diagnosis",
    secondary_diagnosis: "Secondary diagnosis",
    tertiary_diagnosis: "Tertiary diagnosis",
    additional_diagnosis_one: "Additional Diagnosis 1",
    additional_diagnosis_two: "Additional Diagnosis 2",
    disclosure_to_whom: "Disclosure to whom(Address or Fax number)",
    disclosure_type: "Select type",
    purpose_of_disclosure: "Purpose of disclosure",
    type_disclosed: "Type disclosed",
    method_of_disclosure: "Method of disclosure",
    record_of_disclosure_of_phi_ephi: "Record of Disclosure of PHI/EPHI",
    consent_to_charge:
        "I confirm that i received consent from the client, and they are aware they will be charged $60 for this service",
};

// SAFETY PLAN LABELS
export const SAFETY_PLAN_NOTE_LABELS = {
    duration: "Duration",
    diagnosis_codes: "Primary diagnosis",
    secondary_diagnosis: "Secondary diagnosis",
    tertiary_diagnosis: "Tertiary diagnosis",
    additional_diagnosis_one: "Additional Diagnosis 1",
    additional_diagnosis_two: "Additional Diagnosis 2",
    warning_signs:
        "Warning signs (thoughts, images, mood, situation,behavior) that a crisis may be developing",
    internal_coping_strategies:
        "Internal coping strategies - things I can do to take my mind off my problems without contacting another person (relaxation technique, physical activity)",
    people_and_social_settings_that_provide_distraction:
        "People and social settings that provide distraction",
    people_whom_i_can_ask_for_help: "People whom I can ask for help",
    professionals_or_agencies_i_can_contact_during_a_crisis:
        "Professionals or agencies I can contact during a crisis",
    making_the_environment_safe:
        "Making the environment safe (asking clients which means they would consider during a suicidal crisis and collaboratively identify ways to secure or limit access to lethal means)",
    the_one_thing_that_is_important_to_me_and_worth_living_for_is:
        "The one thing that is important to me and worth living for is",
    how_likely_is_the_client_to_use_this_safety_plan:
        "In the event of an emergency/crisis, how likely is the client to use this safety plan?",
    how_likely_is_the_client_to_adhere_to_this_safety_plan:
        "How likely is the client to adhere to this safety plan?",
    detail_hloc_options_and_next_steps:
        "If client is unlikely to adhere, detail HLOC options and next steps",
    where_will_the_client_keep_the_safety_plan_and_how_will_it_be_located_during_a_crisis:
        "Where will the client keep the safety plan and how will it be located during a crisis?",
    is_the_format_appropriate_for_client_capacity_and_circumstances:
        "Is the format appropriate for client's capacity and circumstances?",
    when_should_follow_up_be_done_for_this_client:
        "Client review timeline (when should follow up be done for this client)?",
    frequency_of_sessions: "Frequency of sessions",
    other_frequency_of_sessions: "Please specify if other",
    date_of_next_session: "Date of next session",
    date_created: "Date created",
};

// SOAP PLAN LABELS
export const SOAP_PLAN_NOTE_LABELS = {
    isClientPresent: "Is client present",
    appointment_location: "Appointment location",
    delivery_option: "Client Teletherapy Location",
    other_delivery_option: "Specify other client teletherapy location",
    provider_location: "Provider Teletherapy Location",
    other_provider_location: "Specify other provider teletherapy location",
    consent_was_received_for_the_teletherapy_session:
        "Consent was received for the teletherapy session",
    the_teletherapy_session_was_conducted_via_video:
        "The teletherapy session was conducted via video",
    duration: "Duration",
    diagnosis_codes: "Primary diagnosis",
    secondary_diagnosis: "Secondary diagnosis",
    tertiary_diagnosis: "Tertiary diagnosis",
    additional_diagnosis_one: "Additional Diagnosis 1",
    additional_diagnosis_two: "Additional Diagnosis 2",
    emotional_behavioural_symptoms_demonstrated:
        "Emotional / Behavioural symptoms demonstrated",
    causing: "Causing",
    intention_for_session: "Intention for Session",
    subjective: "Subjective",
    objective: "Objective",
    assessment: "Assessment",
    cognitive_behavioral_therapies: "Cognitive-Behavioral therapies",
    relationship_based_interventions: "Relationship based Interventions",
    other: "Other",
    plan: "Plan",
    frequency_of_sessions: "Frequency of sessions",
    other_frequency_of_sessions: "Please specify if other",
    date_of_next_session: "Date of next session",
    prognosis: "Prognosis",
    client_response: "Client Response",
    goal_one: "Goal one",
    progress_one: "Progress one",
    goal_two: "Goal two",
    progress_two: "Progress two",
    goal_three: "Goal three",
    progress_three: "Progress three",
};

export const INDIVIDUAL_INTAKE_NOTE_DETAILS = {
    appointment_location: "Appointment location",
    delivery_option: "Client Teletherapy Location",
    other_delivery_option: "Specify other client teletherapy location",
    provider_location: "Provider Teletherapy Location",
    other_provider_location: "Specify other provider teletherapy location",
    consent_was_received_for_the_teletherapy_session:
        "Consent was received for the teletherapy session",
    the_teletherapy_session_was_conducted_via_video:
        "The teletherapy session was conducted via video",
    duration: "Duration",
    diagnosis_codes: "Primary diagnosis",
    secondary_diagnosis: "Secondary diagnosis",
    tertiary_diagnosis: "Tertiary diagnosis",
    additional_diagnosis_one: "Additional Diagnosis 1",
    additional_diagnosis_two: "Additional Diagnosis 2",
    gender: "Gender",
    preferred_name: "Preferred name",
    other_gender: "Other gender",
    pronouns: "Pronouns",
    other_sexual_orientation: "Other sexual orientation",
    religion: "Religion",
    education: "Education",
    race: "Race",
    ethinicity: "Ethnicity",
    sexual_orientation: "Sexual orientation",
    current_employment: "Current employment",
    current_employment_details: "Current employment details",
    relationship_status: "Relationship status",
    name_of_partner: "Name of partner",
    emergency_contact_information: "Emergency contact information",
    legal_problems: "Legal problems",
    what_are_the_problem_you_are_seeking_help_for:
        "What are the problem(s) you are seeking help for?",
    anxiety: "Anxiety",
    anxiety_frequency: "Anxiety frequency",
    anxiety_details: "Anxiety details",
    panic_attacks: "Panic attacks",
    panic_attacks_freqency: "Panic attacks frequency",
    panic_attacks_details: "Panic attacks details",
    depression: "Depression",
    depression_freqency: "Depression frequency",
    depression_details: "Depression details",
    other_symptoms: "Other symptoms",
    selected_symptoms: "Selected symptoms",
    physical_symptoms: "Physical symptoms",
    medication_physical_psychiatric: "Medication physical/psychiatric",
    sleeping_habits_and_concerns: "Sleeping habits and concerns",
    difficulties_with_appetite_or_eating_patterns:
        "Difficulties with appetite or eating patterns",
    parents_stepparents_history: "Parents/Stepparents",
    siblings_history: "Siblings",
    grandparents_history: "Grandparents",
    children_history: "Children",
    other_family_members_history: "Other family members",
    family_members_alcohol_abuse: "Alcohol abuse",
    family_members_alcohol_abuse_other: "Other Alcohol abuse",
    family_members_anxiety: "Anxiety",
    family_members_anxiety_other: "Other Anxiety",
    family_members_bipolar_disorder: "Bipolar Disorder",
    family_members_bipolar_disorder_other: "Other Bipolar Disorder",
    family_members_depression: "Depression",
    family_members_depression_other: "Other Depression",
    family_members_ptsd: "PTSD",
    family_members_ptsd_other: "Other PTSD",
    family_members_schizophrenia: "Schizophrenia",
    family_members_schizophrenia_other: "Other Schizophrenia",
    family_members_substance_abuse: "Substance use",
    family_members_substance_abuse_other: "Other Substance abuse",
    family_members_substance_abuse_substance: "Specify substance",
    trauma_abuse_history: "Trauma/abuse history",
    previous_mental_health_treatment_oupatient:
        "Previous Mental Health Treatment outpatient",
    impatient_psychiatric_hospitalization:
        "Inpatient/Psychiatric Hospitalization",
    relevant_medical_history: "Relevant Medical History",
    been_so_distressed_you_seriously_wished_to_end_your_life:
        "Been so distressed you seriously wished to end your life?",
    describe_been_so_distressed_you_seriously_wished_to_end_your_life:
        "Describe the situation",
    has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live:
        "Has anything happened recently to make you feel you don’t want to live?",
    describe_has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live:
        "Describe the situation",
    a_specific_plan_of_how_you_would_kill_yourself:
        "A specific plan of how you would kill yourself?",
    describe_a_specific_plan_of_how_you_would_kill_yourself:
        "Describe the situation",
    access_to_weapons_or_means_of_hurting_self:
        "Access to weapons or means of hurting self?",
    describe_access_to_weapons_or_means_of_hurting_self:
        "Describe the situation",
    made_serious_suicide_attempts: "Made serious suicide attempts",
    describe_made_serious_suicide_attempts: "Describe the situation",
    purposely_done_something_to_hurt_yourself:
        "Purposely done something to hurt yourself?",
    describe_done_something_to_hurt_yourself: "Describe the situation",
    describe_do_you_have_access_to_guns: "Describe the situation",
    heard_voices_telling_you_to_hurt_yourself:
        "Heard voices telling you to hurt yourself?",
    describe_heard_voices_telling_you_to_hurt_yourself:
        "Describe the situation",
    had_relatives_who_attempted_or_commited_sucide:
        "Had relatives who attempted or committed suicide?",
    describe_had_relatives_who_attempted_or_commited_sucide:
        "Describe the situation",
    had_thoughts_of_killing_or_seriously_hurting_someone:
        "Had thoughts of killing or seriously hurting someone?",
    describe_had_thoughts_of_killing_or_seriously_hurting_someone:
        "Describe the situation",
    heard_voices_telling_you_to_hurt_others:
        "Heard voices telling you to hurt others?",
    describe_heard_voices_telling_you_to_hurt_others: "Describe the situation",
    hurt_someone_or_destroyed_property_on_purpose:
        "Hurt someone or destroyed property on purpose?",
    describe_hurt_someone_or_destroyed_property_on_purpose:
        "Describe the situation",
    slapped_kicked_punched_someone_with_intent_to_harm:
        "Slapped, kicked, punched someone with intent to harm?",
    describe_slapped_kicked_punched_someone_with_intent_to_harm:
        "Describe the situation",
    been_arrested_or_detained_for_violent_behavior:
        "Been arrested or detained for violent behavior?",
    describe_been_arrested_or_detained_for_violent_behavior:
        "Describe the situation",
    been_to_jail_for_any_reason: "Been to jail for any reason?",
    describe_been_to_jail_for_any_reason: "Describe the situation",
    been_on_probation_for_any_reason: "Been on probation for any reason?",
    describe_been_on_probation_for_any_reason: "Describe the situation",
    do_you_have_access_to_guns: "Do you have access to guns?",
    mental_health_appearance: "Appearance",
    mental_health_movement: "Movement",
    mental_health_speech_behaviour: "Speech Behavior",
    mental_health_affect_mood: "Affect/Mood",
    mental_health_attention_concentration_effort:
        "Attention/Concentration/effort",
    mental_health_presentation_of_self: "Presentation of self",
    mental_health_cognition:
        "Cognition (decision making, insight, memory, etc)",
    patients_ability_and_capability_to_respond_to_treatment:
        " Patient's ability and capability to respond to treatment",
    details_for_diminished_poor_limited_quality_patient_oriented_evidence:
        "Please provide details for diminished/poor quality patient-orientation",
    people_present_at_session: "Present at session",
    other_person_present_at_session: "Specify Other",
    type_of_treatment_recommended: "Type of treatment recommended",
    treatment_modality_intervention: "Treatment Modality/Intervention(s)",
    other_treatment_modality_intervention: "Specify other treatment/modality",
    frequency_of_sessions: "Frequency of sessions",
    other_frequency_of_sessions: "Please specify if other",
    date_of_next_session: "Date of next session",
    treatment_goal_one: "Treatment goal one",
    treatment_goal_two: "Treatment goal two",
    treatment_goal_three: "Treatment goal three",
    subjective_report: "Subjective report",
    summary: "Summary",
    prognosis: "Prognosis",
    contact_information_for_both_parents:
        "Contact information for both parents",
    name_address_of_personal_physician: "Name/Address of personal physician",
    date_of_last_exam: "Date of last exam",
};

export const MINOR_RISK_ASSESSMENT_LABELS = {
    been_so_distressed_you_seriously_wished_to_end_your_life:
        "Have you ever felt so upset or overwhelmed that you wished you were dead or didn't want to be alive anymore?",
    describe_been_so_distressed_you_seriously_wished_to_end_your_life:
        "Describe the situation",
    has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live:
        "Has something happened recently that made you feel like you don't want to be alive or made you feel hopeless?",
    describe_has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live:
        "What happened?",
    a_specific_plan_of_how_you_would_kill_yourself:
        "Have you thought about a specific way you might try to end your life?",
    describe_a_specific_plan_of_how_you_would_kill_yourself:
        "Describe the situation",
    access_to_weapons_or_means_of_hurting_self:
        "Do you have access to anything you could use to seriously hurt yourself (for example: pills, sharp objects, ropes, firearms, etc.)?",
    describe_access_to_weapons_or_means_of_hurting_self:
        "Describe the situation",
    made_serious_suicide_attempts:
        "Have you ever tried to end your life in the past?",
    describe_made_serious_suicide_attempts: "When did this happen?",
    purposely_done_something_to_hurt_yourself:
        "Have you ever hurt yourself on purpose (for example: cutting, burning, hitting yourself, scratching until you bleed, etc.)?",
    describe_done_something_to_hurt_yourself: "Please describe what happened.",
    describe_do_you_have_access_to_guns: "Describe the situation",
    heard_voices_telling_you_to_hurt_yourself:
        "Have you ever heard or felt like you heard a voice (when no one was there) telling you to hurt yourself?",
    describe_heard_voices_telling_you_to_hurt_yourself:
        "Describe the situation",
    had_relatives_who_attempted_or_commited_sucide:
        "Has anyone in your family ever tried to end their life or died by suicide?",
    describe_had_relatives_who_attempted_or_commited_sucide:
        "Describe the situation",
    had_thoughts_of_killing_or_seriously_hurting_someone:
        "Have you ever had serious thoughts about hurting or killing someone else?",
    describe_had_thoughts_of_killing_or_seriously_hurting_someone:
        "Describe the situation",
    heard_voices_telling_you_to_hurt_others:
        "Have you ever heard a voice (when no one was there) telling you to hurt someone else?",
    describe_heard_voices_telling_you_to_hurt_others: "Describe the situation",
    hurt_someone_or_destroyed_property_on_purpose:
        "Have you ever purposely hurt someone or seriously damaged someone's property?",
    describe_hurt_someone_or_destroyed_property_on_purpose:
        "Describe the situation",
    slapped_kicked_punched_someone_with_intent_to_harm:
        "Have you ever hit, kicked, punched, or physically attacked someone on purpose?",
    describe_slapped_kicked_punched_someone_with_intent_to_harm:
        "Describe the situation",
    been_arrested_or_detained_for_violent_behavior:
        "Have you ever been arrested, detained, or suspended from school because of violent behavior?",
    describe_been_arrested_or_detained_for_violent_behavior:
        "Describe the situation",
    been_to_jail_for_any_reason:
        "Have you ever been in juvenile detention, jail, or a correctional facility?",
    describe_been_to_jail_for_any_reason: "Describe the situation",
    been_on_probation_for_any_reason:
        "Have you ever been on probation or involved with the juvenile justice system?",
    describe_been_on_probation_for_any_reason: "Describe the situation",
    do_you_have_access_to_guns:
        "Are there any guns in your home or somewhere you can easily access?",
    ever_run_away_from_home:
        "Have you ever run away from home or seriously thought about running away?",
    describe_ever_run_away_from_home: "Describe the situation",
    ever_been_bullied_or_bullied_someone:
        "Have you ever been bullied or bullied someone else in a way that led to threats or safety concerns?",
    describe_ever_been_bullied_or_bullied_someone: "Describe the situation",
};

export const INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS = {
    appointment_location: "Appointment location",
    delivery_option: "Client Teletherapy Location",
    other_delivery_option: "Specify other client teletherapy location",
    provider_location: "Provider Teletherapy Location",
    other_provider_location: "Specify other provider teletherapy location",
    consent_was_received_for_the_teletherapy_session:
        "Consent was received for the teletherapy session",
    the_teletherapy_session_was_conducted_via_video:
        "The teletherapy session was conducted via video",
    duration: "Duration",
    diagnosis_codes: "Primary diagnosis",
    secondary_diagnosis: "Secondary diagnosis",
    tertiary_diagnosis: "Tertiary diagnosis",
    additional_diagnosis_one: "Additional Diagnosis 1",
    additional_diagnosis_two: "Additional Diagnosis 2",
    gender: "Gender",
    preferred_name: "Preferred name",
    other_gender: "Other gender",
    pronouns: "Pronouns",
    other_sexual_orientation: "Other sexual orientation",
    religion: "Religion",
    education: "Education",
    race: "Race",
    ethinicity: "Ethnicity",
    sexual_orientation: "Sexual orientation",
    current_employment: "Current employment",
    current_employment_details: "Current employment details",
    relationship_status: "Relationship status",
    name_of_partner: "Name of partner",
    emergency_contact_information: "Emergency contact information",
    legal_problems: "Legal problems",
    what_are_the_problem_you_are_seeking_help_for:
        "What are the problem(s) you are seeking help for?",
    anxiety: "Anxiety",
    anxiety_frequency: "Anxiety frequency",
    anxiety_details: "Anxiety details",
    panic_attacks: "Panic attacks",
    panic_attacks_frequency: "Panic attacks frequency",
    panic_attacks_details: "Panic attacks details",
    depression: "Depression",
    depression_frequency: "Depression frequency",
    depression_details: "Depression details",
    other_symptoms: "Other symptoms",
    selected_symptoms: "Selected symptoms",
    physical_symptoms: "Physical symptoms",
    medication_physical_psychiatric: "Medication physical/psychiatric",
    sleeping_habits_and_concerns: "Sleeping habits and concerns",
    difficulties_with_appetite_or_eating_patterns:
        "Difficulties with appetite or eating patterns",
    parents_stepparents_history: "Parents/Stepparents",
    siblings_history: "Siblings",
    grandparents_history: "Grandparents",
    children_history: "Children",
    other_family_members_history: "Other family members",
    family_members_alcohol_abuse: "Alcohol abuse",
    family_members_alcohol_abuse_other: "Other Alcohol abuse",
    family_members_anxiety: "Anxiety",
    family_members_anxiety_other: "Other Anxiety",
    family_members_bipolar_disorder: "Bipolar Disorder",
    family_members_bipolar_disorder_other: "Other Bipolar Disorder",
    family_members_depression: "Depression",
    family_members_depression_other: "Other Depression",
    family_members_ptsd: "PTSD",
    family_members_ptsd_other: "Other PTSD",
    family_members_schizophrenia: "Schizophrenia",
    family_members_schizophrenia_other: "Other Schizophrenia",
    family_members_substance_abuse: "Substance use",
    family_members_substance_abuse_other: "Other Substance abuse",
    family_members_substance_abuse_substance: "Specify substance",
    trauma_abuse_history: "Trauma/abuse history",
    previous_mental_health_treatment_oupatient:
        "Previous Mental Health Treatment outpatient",
    impatient_psychiatric_hospitalization:
        "Inpatient/Psychiatric Hospitalization",
    relevant_medical_history: "Relevant Medical History",
    been_so_distressed_you_seriously_wished_to_end_your_life:
        "Been so distressed you seriously wished to end your life?",
    describe_been_so_distressed_you_seriously_wished_to_end_your_life:
        "Describe the situation",
    has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live:
        "Has anything happened recently to make you feel you don’t want to live?",
    describe_has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live:
        "Describe the situation",
    a_specific_plan_of_how_you_would_kill_yourself:
        "A specific plan of how you would kill yourself?",
    describe_a_specific_plan_of_how_you_would_kill_yourself:
        "Describe the situation",
    access_to_weapons_or_means_of_hurting_self:
        "Access to weapons or means of hurting self?",
    describe_access_to_weapons_or_means_of_hurting_self:
        "Describe the situation",
    made_serious_suicide_attempts: "Made serious suicide attempts",
    describe_made_serious_suicide_attempts: "Describe the situation",
    purposely_done_something_to_hurt_yourself:
        "Purposely done something to hurt yourself?",
    describe_done_something_to_hurt_yourself: "Describe the situation",
    describe_do_you_have_access_to_guns: "Describe the situation",
    heard_voices_telling_you_to_hurt_yourself:
        "Heard voices telling you to hurt yourself?",
    describe_heard_voices_telling_you_to_hurt_yourself:
        "Describe the situation",
    had_relatives_who_attempted_or_committed_suicide:
        "Had relatives who attempted or committed suicide?",
    describe_had_relatives_who_attempted_or_committed_suicide:
        "Describe the situation",
    had_thoughts_of_killing_or_seriously_hurting_someone:
        "Had thoughts of killing or seriously hurting someone?",
    describe_had_thoughts_of_killing_or_seriously_hurting_someone:
        "Describe the situation",
    heard_voices_telling_you_to_hurt_others:
        "Heard voices telling you to hurt others?",
    describe_heard_voices_telling_you_to_hurt_others: "Describe the situation",
    hurt_someone_or_destroyed_property_on_purpose:
        "Hurt someone or destroyed property on purpose?",
    describe_hurt_someone_or_destroyed_property_on_purpose:
        "Describe the situation",
    slapped_kicked_punched_someone_with_intent_to_harm:
        "Slapped, kicked, punched someone with intent to harm?",
    describe_slapped_kicked_punched_someone_with_intent_to_harm:
        "Describe the situation",
    been_arrested_or_detained_for_violent_behavior:
        "Been arrested or detained for violent behavior?",
    describe_been_arrested_or_detained_for_violent_behavior:
        "Describe the situation",
    been_to_jail_for_any_reason: "Been to jail for any reason?",
    describe_been_to_jail_for_any_reason: "Describe the situation",
    been_on_probation_for_any_reason: "Been on probation for any reason?",
    describe_been_on_probation_for_any_reason: "Describe the situation",
    do_you_have_access_to_guns: "Do you have access to guns?",
    mental_health_appearance: "Appearance",
    mental_health_movement: "Movement",
    mental_health_speech_behaviour: "Speech Behavior",
    mental_health_affect_mood: "Affect/Mood",
    mental_health_attention_concentration_effort:
        "Attention/Concentration/effort",
    mental_health_presentation_of_self: "Presentation of self",
    mental_health_cognition:
        "Cognition (decision making, insight, memory, etc)",
    patients_ability_and_capability_to_respond_to_treatment:
        " Patient's ability and capability to respond to treatment",
    details_for_diminished_poor_limited_quality_patient_oriented_evidence:
        "Please provide details for diminished/poor quality patient-orientation",
    people_present_at_session: "Present at session",
    other_person_present_at_session: "Specify Other",
    type_of_treatment_recommended: "Type of treatment recommended",
    treatment_modality_intervention: "Treatment Modality/Intervention(s)",
    other_treatment_modality_intervention: "Specify other treatment/modality",
    frequency_of_sessions: "Frequency of sessions",
    other_frequency_of_sessions: "Please specify if other",
    date_of_next_session: "Date of next session",
    treatment_goal_one: "Treatment goal one",
    treatment_goal_two: "Treatment goal two",
    treatment_goal_three: "Treatment goal three",
    subjective_report: "Subjective report",
    summary: "Summary",
    prognosis: "Prognosis",
    contact_information_for_both_parents:
        "Contact information for both parents",
    name_address_of_personal_physician: "Name/Address of personal physician",
    date_of_last_exam: "Date of last exam",
    supervisory_intake_statement: "Supervisory intake statement",
    medical_necessity_rationale: "Medical necessity rationale",
    risk_review_supervision_plan: "Risk Review + Supervision Plan",
    formal_safety_plan_details: "Formal safety plan details",
    higher_level_of_care_details: "Higher level of care details",
};

export const SUPERVISOR_DIAGNOSTIC_REVIEW_NOTE_DETAILS = {
    date_of_review: "Date of review",
    diagnosis_codes: "Current diagnosis (prior to change)",
    revised_diagnosis_codes: "Revised diagnosis (post-review)",
    new_clinical_information_supporting_diagnostic_change:
        "New Clinical Information Supporting Diagnostic Change",
    diagnostic_rationale: "Diagnostic rationale",
    reviewed_and_approved_by_lp: "Reviewed and approved by LP",
};

export const FAMILY_INTAKE_NOTE_DETAILS = {
    isClientPresent: "Is client present",
    appointment_location: "Appointment location",
    delivery_option: "Client Teletherapy Location",
    other_delivery_option: "Specify other client teletherapy location",
    provider_location: "Provider Teletherapy Location",
    other_provider_location: "Specify other provider teletherapy location",
    consent_was_received_for_the_teletherapy_session:
        "Consent was received for the teletherapy session",
    the_teletherapy_session_was_conducted_via_video:
        "The teletherapy session was conducted via video",
    duration: "Duration",
    diagnosis_codes: "Primary diagnosis",
    secondary_diagnosis: "Secondary diagnosis",
    tertiary_diagnosis: "Tertiary diagnosis",
    additional_diagnosis_one: "Additional Diagnosis 1",
    additional_diagnosis_two: "Additional Diagnosis 2",
    what_are_the_problem_you_are_seeking_help_for:
        "What are the problem(s) you are seeking help for?",
    people_present_at_session: "Present at session",
    other_person_present_at_session: "Specify Other",
    type_of_treatment_recommended: "Type of treatment recommended",
    treatment_modality_intervention: "Treatment Modality/Intervention(s)",
    other_treatment_modality_intervention: "Specify other treatment/modality",
    treatment_goal_one: "Treatment goal one",
    treatment_goal_two: "Treatment goal two",
    treatment_goal_three: "Treatment goal three",
    subjective_report: "Subjective report",
    summary: "Summary",
    prognosis: "Prognosis",
    frequency_of_sessions: "Frequency of sessions",
    other_frequency_of_sessions: "Please specify if other",
    date_of_next_session: "Date of next session",
};

export const FAMILY_INTAKE_FAMILY_NOTE_DETAILS = {
    name: "Name",
    gender: "Gender",
    preferred_name: "Preferred name",
    other_gender: "Other gender",
    pronouns: "Pronouns",
    other_sexual_orientation: "Other sexual orientation",
    religion: "Religion",
    education: "Education",
    race: "Race",
    ethinicity: "Ethnicity",
    sexual_orientation: "Sexual orientation",
    current_employment: "Current employment",
    current_employment_details: "Current employment details",
    relationship_status: "Relationship status",
    name_of_partner: "Name of partner",
    emergency_contact_information: "Emergency contact information",
    legal_problems: "Legal problems",
    anxiety: "Anxiety",
    anxiety_frequency: "Anxiety frequency",
    anxiety_details: "Anxiety details",
    panic_attacks: "Panic attacks",
    panic_attacks_freqency: "Panic attacks frequency",
    panic_attacks_details: "Panic attacks details",
    depression: "Depression",
    depression_freqency: "Depression frequency",
    depression_details: "Depression details",
    other_symptoms: "Other symptoms",
    selected_symptoms: "Selected symptoms",
    physical_symptoms: "Physical symptoms",
    medication_physical_psychiatric: "Medication physical/psychiatric",
    sleeping_habits_and_concerns: "Sleeping habits and concerns",
    difficulties_with_appetite_or_eating_patterns:
        "Difficulties with appetite or eating patterns",
    parents_stepparents_history: "Parents/Stepparents",
    siblings_history: "Siblings",
    grandparents_history: "Grandparents",
    children_history: "Children",
    other_family_members_history: "Other family members",
    family_members_alcohol_abuse: "Alcohol abuse",
    family_members_alcohol_abuse_other: "Other Alcohol abuse",
    family_members_anxiety: "Anxiety",
    family_members_anxiety_other: "Other Anxiety",
    family_members_bipolar_disorder: "Bipolar Disorder",
    family_members_bipolar_disorder_other: "Other Bipolar Disorder",
    family_members_depression: "Depression",
    family_members_depression_other: "Other Depression",
    family_members_ptsd: "PTSD",
    family_members_ptsd_other: "Other PTSD",
    family_members_schizophrenia: "Schizophrenia",
    family_members_schizophrenia_other: "Other Schizophrenia",
    family_members_substance_abuse: "Substance use",
    family_members_substance_abuse_other: "Other Substance abuse",
    family_members_substance_abuse_substance: "Specify substance",
    trauma_abuse_history: "Trauma/abuse history",
    previous_mental_health_treatment_oupatient:
        "Previous Mental Health Treatment outpatient",
    impatient_psychiatric_hospitalization:
        "Impatient/Psychiatric Hospitalization",
    relevant_medical_history: "Relevant Medical History",
    been_so_distressed_you_seriously_wished_to_end_your_life:
        "Been so distressed you seriously wished to end your life?",
    describe_been_so_distressed_you_seriously_wished_to_end_your_life:
        "Describe the situation",
    has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live:
        "Has anything happened recently to make you feel you don’t want to live?",
    describe_has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live:
        "Describe the situation",
    a_specific_plan_of_how_you_would_kill_yourself:
        "A specific plan of how you would kill yourself?",
    describe_a_specific_plan_of_how_you_would_kill_yourself:
        "Describe the situation",
    access_to_weapons_or_means_of_hurting_self:
        "Access to weapons or means of hurting self?",
    describe_access_to_weapons_or_means_of_hurting_self:
        "Describe the situation",
    made_serious_suicide_attempts: "Made serious suicide attempts",
    describe_made_serious_suicide_attempts: "Describe the situation",
    purposely_done_something_to_hurt_yourself:
        "Purposely done something to hurt yourself?",
    describe_done_something_to_hurt_yourself: "Describe the situation",
    heard_voices_telling_you_to_hurt_yourself:
        "Heard voices telling you to hurt yourself?",
    describe_heard_voices_telling_you_to_hurt_yourself:
        "Describe the situation",
    had_relatives_who_attempted_or_commited_sucide:
        "Had relatives who attempted or committed suicide?",
    describe_had_relatives_who_attempted_or_commited_sucide:
        "Describe the situation",
    had_thoughts_of_killing_or_seriously_hurting_someone:
        "Had thoughts of killing or seriously hurting someone?",
    describe_had_thoughts_of_killing_or_seriously_hurting_someone:
        "Describe the situation",
    heard_voices_telling_you_to_hurt_others:
        "Heard voices telling you to hurt others?",
    describe_heard_voices_telling_you_to_hurt_others: "Describe the situation",
    hurt_someone_or_destroyed_property_on_purpose:
        "Hurt someone or destroyed property on purpose?",
    describe_hurt_someone_or_destroyed_property_on_purpose:
        "Describe the situation",
    slapped_kicked_punched_someone_with_intent_to_harm:
        "Slapped, kicked, punched someone with intent to harm?",
    describe_slapped_kicked_punched_someone_with_intent_to_harm:
        "Describe the situation",
    been_arrested_or_detained_for_violent_behavior:
        "Been arrested or detained for violent behavior?",
    describe_been_arrested_or_detained_for_violent_behavior:
        "Describe the situation",
    been_to_jail_for_any_reason: "Been to jail for any reason?",
    describe_been_to_jail_for_any_reason: "Describe the situation",
    been_on_probation_for_any_reason: "Been on probation for any reason?",
    describe_been_on_probation_for_any_reason: "Describe the situation",
    do_you_have_access_to_guns: "Do you have access to guns?",
    describe_do_you_have_access_to_guns: "Describe the situation",
    mental_health_appearance: "Appearance",
    mental_health_movement: "Movement",
    mental_health_speech_behaviour: "Speech Behavior",
    mental_health_affect_mood: "Affect/Mood",
    mental_health_attention_concentration_effort:
        "Attention/Concentration/effort",
    mental_health_presentation_of_self: "Presentation of self",
    mental_health_cognition:
        "Cognition (decision making, insight, memory, etc)",
    patients_ability_and_capability_to_respond_to_treatment:
        " Patient's ability and capability to respond to treatment",
    details_for_diminished_poor_limited_quality_patient_oriented_evidence:
        "Please provide details for diminished/poor quality patient-orientation",
};

export const FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS = {
    isClientPresent: "Is client present",
    appointment_location: "Appointment location",
    delivery_option: "Client Teletherapy Location",
    other_delivery_option: "Specify other client teletherapy location",
    provider_location: "Provider Teletherapy Location",
    other_provider_location: "Specify other provider teletherapy location",
    consent_was_received_for_the_teletherapy_session:
        "Consent was received for the teletherapy session",
    the_teletherapy_session_was_conducted_via_video:
        "The teletherapy session was conducted via video",
    duration: "Duration",
    diagnosis_codes: "Primary diagnosis",
    secondary_diagnosis: "Secondary diagnosis",
    tertiary_diagnosis: "Tertiary diagnosis",
    additional_diagnosis_one: "Additional Diagnosis 1",
    additional_diagnosis_two: "Additional Diagnosis 2",
    what_are_the_problem_you_are_seeking_help_for:
        "What are the problem(s) you are seeking help for?",
    people_present_at_session: "Present at session",
    other_person_present_at_session: "Specify Other",
    type_of_treatment_recommended: "Type of treatment recommended",
    treatment_modality_intervention: "Treatment Modality/Intervention(s)",
    other_treatment_modality_intervention: "Specify other treatment/modality",
    treatment_goal_one: "Treatment goal one",
    treatment_goal_two: "Treatment goal two",
    treatment_goal_three: "Treatment goal three",
    subjective_report: "Subjective report",
    summary: "Summary",
    prognosis: "Prognosis",
    frequency_of_sessions: "Frequency of sessions",
    other_frequency_of_sessions: "Please specify if other",
    date_of_next_session: "Date of next session",
    supervisory_intake_statement: "Supervisory intake statement",
    medical_necessity_rationale: "Medical necessity rationale",
    risk_review_supervision_plan: "Risk Review + Supervision Plan",
    formal_safety_plan_details: "Formal safety plan details",
    higher_level_of_care_details: "Higher level of care details",
};

export const FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_FAMILY_NOTE_DETAILS = {
    name: "Name",
    gender: "Gender",
    preferred_name: "Preferred name",
    other_gender: "Other gender",
    pronouns: "Pronouns",
    other_sexual_orientation: "Other sexual orientation",
    religion: "Religion",
    education: "Education",
    race: "Race",
    ethinicity: "Ethnicity",
    sexual_orientation: "Sexual orientation",
    current_employment: "Current employment",
    current_employment_details: "Current employment details",
    relationship_status: "Relationship status",
    name_of_partner: "Name of partner",
    emergency_contact_information: "Emergency contact information",
    legal_problems: "Legal problems",
    anxiety: "Anxiety",
    anxiety_frequency: "Anxiety frequency",
    anxiety_details: "Anxiety details",
    panic_attacks: "Panic attacks",
    panic_attacks_freqency: "Panic attacks frequency",
    panic_attacks_details: "Panic attacks details",
    depression: "Depression",
    depression_freqency: "Depression frequency",
    depression_details: "Depression details",
    other_symptoms: "Other symptoms",
    selected_symptoms: "Selected symptoms",
    physical_symptoms: "Physical symptoms",
    medication_physical_psychiatric: "Medication physical/psychiatric",
    sleeping_habits_and_concerns: "Sleeping habits and concerns",
    difficulties_with_appetite_or_eating_patterns:
        "Difficulties with appetite or eating patterns",
    parents_stepparents_history: "Parents/Stepparents",
    siblings_history: "Siblings",
    grandparents_history: "Grandparents",
    children_history: "Children",
    other_family_members_history: "Other family members",
    family_members_alcohol_abuse: "Alcohol abuse",
    family_members_alcohol_abuse_other: "Other Alcohol abuse",
    family_members_anxiety: "Anxiety",
    family_members_anxiety_other: "Other Anxiety",
    family_members_bipolar_disorder: "Bipolar Disorder",
    family_members_bipolar_disorder_other: "Other Bipolar Disorder",
    family_members_depression: "Depression",
    family_members_depression_other: "Other Depression",
    family_members_ptsd: "PTSD",
    family_members_ptsd_other: "Other PTSD",
    family_members_schizophrenia: "Schizophrenia",
    family_members_schizophrenia_other: "Other Schizophrenia",
    family_members_substance_abuse: "Substance use",
    family_members_substance_abuse_other: "Other Substance abuse",
    family_members_substance_abuse_substance: "Specify substance",
    trauma_abuse_history: "Trauma/abuse history",
    previous_mental_health_treatment_oupatient:
        "Previous Mental Health Treatment outpatient",
    impatient_psychiatric_hospitalization:
        "Inpatient/Psychiatric Hospitalization",
    relevant_medical_history: "Relevant Medical History",
    been_so_distressed_you_seriously_wished_to_end_your_life:
        "Been so distressed you seriously wished to end your life?",
    describe_been_so_distressed_you_seriously_wished_to_end_your_life:
        "Describe the situation",
    has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live:
        "Has anything happened recently to make you feel you don't want to live?",
    describe_has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live:
        "Describe the situation",
    a_specific_plan_of_how_you_would_kill_yourself:
        "A specific plan of how you would kill yourself?",
    describe_a_specific_plan_of_how_you_would_kill_yourself:
        "Describe the situation",
    access_to_weapons_or_means_of_hurting_self:
        "Access to weapons or means of hurting self?",
    describe_access_to_weapons_or_means_of_hurting_self:
        "Describe the situation",
    made_serious_suicide_attempts: "Made serious suicide attempts",
    describe_made_serious_suicide_attempts: "Describe the situation",
    purposely_done_something_to_hurt_yourself:
        "Purposely done something to hurt yourself?",
    describe_done_something_to_hurt_yourself: "Describe the situation",
    heard_voices_telling_you_to_hurt_yourself:
        "Heard voices telling you to hurt yourself?",
    describe_heard_voices_telling_you_to_hurt_yourself:
        "Describe the situation",
    had_relatives_who_attempted_or_commited_sucide:
        "Had relatives who attempted or committed suicide?",
    describe_had_relatives_who_attempted_or_commited_sucide:
        "Describe the situation",
    had_thoughts_of_killing_or_seriously_hurting_someone:
        "Had thoughts of killing or seriously hurting someone?",
    describe_had_thoughts_of_killing_or_seriously_hurting_someone:
        "Describe the situation",
    heard_voices_telling_you_to_hurt_others:
        "Heard voices telling you to hurt others?",
    describe_heard_voices_telling_you_to_hurt_others: "Describe the situation",
    hurt_someone_or_destroyed_property_on_purpose:
        "Hurt someone or destroyed property on purpose?",
    describe_hurt_someone_or_destroyed_property_on_purpose:
        "Describe the situation",
    slapped_kicked_punched_someone_with_intent_to_harm:
        "Slapped, kicked, punched someone with intent to harm?",
    describe_slapped_kicked_punched_someone_with_intent_to_harm:
        "Describe the situation",
    been_arrested_or_detained_for_violent_behavior:
        "Been arrested or detained for violent behavior?",
    describe_been_arrested_or_detained_for_violent_behavior:
        "Describe the situation",
    been_to_jail_for_any_reason: "Been to jail for any reason?",
    describe_been_to_jail_for_any_reason: "Describe the situation",
    been_on_probation_for_any_reason: "Been on probation for any reason?",
    describe_been_on_probation_for_any_reason: "Describe the situation",
    do_you_have_access_to_guns: "Do you have access to guns?",
    describe_do_you_have_access_to_guns: "Describe the situation",
    mental_health_appearance: "Appearance",
    mental_health_movement: "Movement",
    mental_health_speech_behaviour: "Speech Behavior",
    mental_health_affect_mood: "Affect/Mood",
    mental_health_attention_concentration_effort:
        "Attention/Concentration/effort",
    mental_health_presentation_of_self: "Presentation of self",
    mental_health_cognition:
        "Cognition (decision making, insight, memory, etc)",
    patients_ability_and_capability_to_respond_to_treatment:
        " Patient's ability and capability to respond to treatment",
    details_for_diminished_poor_limited_quality_patient_oriented_evidence:
        "Please provide details for diminished/poor quality patient-orientation",
};

/**
 * SECTIONS IDS FOR DIFFERENT NOTE TYPES
 */

export const INTAKE_SECTIONS = {
    identity: "identity",
    "presenting concerns": "presenting_concerns",
    symptoms: "symptoms",
    "family history": "family_history",
    "family psychiatric history": "family_psychiatric_history",
    "mental health history": "mental_health_history",
    "suicide risk assessment": "suicide_risk_assessment",
    "mental status exam": "mental_status_exam",
    summary: "summary",
    "treatment goals": "treatment_goals",
};

export const SOAP_NOTE_SECTIONS = {
    "session note": "session_note",
    "therapy intervention techniques": "therapy_intervention_techniques",
    "treatment plan/ progress": "treatment_plan_progress",
};

export const TREATMENT_REVIEW_NOTE_SECTIONS = {
    identity: "identity",
    symptoms: "symptoms",
    "suicide risk assessment": "suicide_risk_assessment",
    summary: "summary",
    "treatment goals": "treatment_goals",
};

export const STANDARD_TESTING_NOTE_LABELS = {
    duration: "Duration",
    diagnosis_codes: "Primary diagnosis as derived from clinician assessment",
    secondary_diagnosis:
        "Secondary diagnosis as derived from clinician assessment",
    tertiary_diagnosis:
        "Tertiary diagnosis as derived from clinician assessment",
    additional_diagnosis_one: "Additional Diagnosis 1",
    additional_diagnosis_two: "Additional Diagnosis 2",
    summary: "Summary",
    files: "Files",
};

export const PAUSE_NOTIFICATION_PSYCH_NOTE_LABELS = {
    pause_reason:
        "Please indicate reason client is currently on pause for treatment",
    files: "Files",
};

export const NO_SHOW_PSYCH_NOTE_LABELS = {
    missed_appointment_type:
        "Which kind of appointment did the client not show up for?",
    no_show_reason: "Reason provided by client / when notice was given",
    files: "Files",
};

export const COMMUNICATION_WITH_CLIENT_NOTE_LABELS = {
    communication_details: "Details of communication with client",
    files: "Files",
};

export const FORMS_AND_DOCUMENTATION_NOTE_LABELS = {
    forms_and_documentation_details:
        "Details of forms and documentation completed",
    files: "Files",
};

export const RECORD_OF_DISCLOSURE_PSYCH_NOTE_LABELS = {
    duration: "Duration",
    diagnosis_codes: "Primary diagnosis",
    secondary_diagnosis: "Secondary diagnosis",
    tertiary_diagnosis: "Tertiary diagnosis",
    additional_diagnosis_one: "Additional Diagnosis 1",
    additional_diagnosis_two: "Additional Diagnosis 2",
    disclosure_to_whom: "Disclosure to whom(Address or Fax number)",
    disclosure_type: "Select type",
    purpose_of_disclosure: "Description Disclosure Purposes of disclosure",
    type_disclosed: "Type disclosed",
    method_of_disclosure: "How Disclosure is made",
    other_method_of_disclosure: "Other",
    record_of_disclosure_of_phi_ephi: "Record of Disclosure of PHI/EPHI",
    files: "Files",
};

export const TERMINATION_OF_TREATMENT_NOTE_LABELS = {
    appointment_location: "Appointment location",
    delivery_option: "Client Teletherapy Location",
    other_delivery_option: "Specify other client teletherapy location",
    provider_location: "Provider Teletherapy Location",
    other_provider_location: "Specify other provider teletherapy location",
    consent_was_received_for_the_teletherapy_session:
        "Consent was received for the teletherapy session",
    the_teletherapy_session_was_conducted_via_video:
        "The teletherapy session was conducted via video",
    duration: "Duration",
    first_session_date: "Date of first session",
    last_session_date: "Date of last session",
    total_number_of_sessions:
        "Total number of therapy sessions or length of treatment",
    diagnosis_codes: "Primary diagnosis",
    secondary_diagnosis: "Secondary diagnosis",
    tertiary_diagnosis: "Tertiary diagnosis",
    additional_diagnosis_one: "Additional Diagnosis 1",
    additional_diagnosis_two: "Additional Diagnosis 2",
    presenting_problems: "Presenting problems",
    planned_treatment_and_goals: "Planned treatment and goals",
    course_of_treatment: "Course of treatment",
    patient_final_condition: "Patient final condition",
    prognosis: "Prognosis",
    reason_for_termination:
        "Reason for termination (e.g. met goals, patient moved or transferred care)",
    discharge_plan_and_follow_up:
        "Discharge plan and follow-up (recommendations, referrals, post-termination contacts)",
    treatment_modality_intervention: "Modality",
    other_treatment_modality_intervention: "Specify Other",
    files: "Files",
};

export const INITIAL_SOAP_NOTE_LABELS = {
    isClientPresent: "Is client present",
    appointment_location: "Appointment location",
    delivery_option: "Client Teletherapy Location",
    other_delivery_option: "Specify other client teletherapy location",
    provider_location: "Provider Teletherapy Location",
    other_provider_location: "Specify other provider teletherapy location",
    consent_was_received_for_the_teletherapy_session:
        "Consent was received for the teletherapy session",
    the_teletherapy_session_was_conducted_via_video:
        "The teletherapy session was conducted via video",
    duration: "Duration",
    diagnosis_codes: "Primary diagnosis",
    secondary_diagnosis: "Secondary diagnosis",
    tertiary_diagnosis: "Tertiary diagnosis",
    additional_diagnosis_one: "Additional Diagnosis 1",
    additional_diagnosis_two: "Additional Diagnosis 2",
    emotional_behavioural_symptoms_demonstrated:
        "Emotional / Behavioural symptoms demonstrated",
    causing: "Causing",
    intention_for_session: "Intention for Session",
    subjective: "Subjective",
    objective: "Objective",
    assessment: "Assessment",
    cognitive_behavioral_therapies: "Cognitive-Behavioral therapies",
    relationship_based_interventions: "Relationship based Interventions",
    other: "Other",
    plan: "Plan",
    frequency_of_sessions: "Frequency of sessions",
    other_frequency_of_sessions: "Please specify if other",
    date_of_next_session: "Date of next session",
    prognosis: "Prognosis",
    client_response: "Client Response",
    goal_one: "Goal one",
    progress_one: "Progress one",
    goal_two: "Goal two",
    progress_two: "Progress two",
    goal_three: "Goal three",
    progress_three: "Progress three",
    risk_and_safety_language: "Risk and safety language",
    new_risk_concerns_details: "New risk concerns details",
    additional_client_reported_information_post_intake:
        "Additional client reported information (Post-Intake)",
};

export const PSYCHOSOCIAL_ASSESSMENT_ALERT_TEXT = `Non-Billable Supervisory Training Note \u2014 This note reflects psychosocial information gathered by the LP for training purposes. Diagnostic intake, medical necessity, and treatment plan were established by the supervising clinician. This note does not constitute a diagnostic evaluation.`;

export const CONSULTATION_NOTE_MED_LABELS = {
    session_location: "Session location",
    prolonged_care_different_day_as_em_visit:
        "Prolonged Care Different Day as E/M visit",
    details_of_prolonged_care_different_day_as_em_visit:
        "Details of prolonged care",
    modality: "Modality",
    total_amount_of_time: "Total amount of time",
    condition_type: "Condition Type",
    condition_type_additional_details: "Condition Type Additional Details",
    condition_status: "Condition Status",
    condition_status_additional_details: "Condition Status Additional Details",
    location_of_the_client: "Client telehealth location",
    other_location_of_the_client: "Other client telehealth location",
    location_of_the_provider: "Provider telehealth location",
    other_location_of_the_provider: "Other provider telehealth location",
    consent_was_received_for_the_teletherapy_session:
        "Consent was received for the teletherapy session",
    the_session_was_conducted_via_video: "The session was conducted via video",
};

export const DOCUMENTATION_REQUEST_NOTE_MED_LABELS = {
    session_location: "Session location",
    diagnosis_codes: "Primary diagnosis",
    secondary_diagnosis: "Secondary diagnosis",
    tertiary_diagnosis: "Tertiary diagnosis",
    additional_diagnosis_one: "Additional Diagnosis 1",
    additional_diagnosis_two: "Additional Diagnosis 2",
    record_of_disclosure_of_phi_ephi: "Record of Disclosure of PHI/EPHI",
    disclosure_to_whom: "Disclosure to Whom (Address or Fax Number)",
    purpose_of_disclosure: "Purpose of Disclosure",
    description_disclosure_purpose_of_disclosure:
        "Description Disclosure Purposes of Disclosure",
    type_disclosed: "Type disclosed",
    how_disclosure_is_made: "How Disclosure is made",
    other_how_disclosure_is_made: "Other how disclosure is made",
    start_time: "Start Time",
    end_time: "End Time",
    files: "Files",
    location_of_the_client: "Client telehealth location",
    other_location_of_the_client: "Other client telehealth location",
    location_of_the_provider: "Provider telehealth location",
    other_location_of_the_provider: "Other provider telehealth location",
    consent_was_received_for_the_teletherapy_session:
        "Consent was received for the teletherapy session",
    the_session_was_conducted_via_video: "The session was conducted via video",
};

export const EVALUATION_SUBMISSION_NOTE_MED_LABELS = {
    session_location: "Session location",
    date: "Date",
    start_time: "Start Time",
    end_time: "End Time",
    files: "Files",
    diagnosis_codes: "Primary diagnosis",
    secondary_diagnosis: "Secondary diagnosis",
    tertiary_diagnosis: "Tertiary diagnosis",
    additional_diagnosis_one: "Additional Diagnosis 1",
    additional_diagnosis_two: "Additional Diagnosis 2",
    summary: "Summary",
    name_of_provider_therapist: "Name of Provider/Therapist",
    location_of_the_client: "Client telehealth location",
    other_location_of_the_client: "Other client telehealth location",
    location_of_the_provider: "Provider telehealth location",
    other_location_of_the_provider: "Other provider telehealth location",
    consent_was_received_for_the_teletherapy_session:
        "Consent was received for the teletherapy session",
    the_session_was_conducted_via_video: "The session was conducted via video",
};

export const NO_SHOW_NOTE_MED_LABELS = {
    session_location: "Session location",
    reason_provided_by_client_and_when_notice_was_given:
        "Reason provided by client / when notice was given",
    location_of_the_client: "Client telehealth location",
    other_location_of_the_client: "Other client telehealth location",
    location_of_the_provider: "Provider telehealth location",
    other_location_of_the_provider: "Other provider telehealth location",
    consent_was_received_for_the_teletherapy_session:
        "Consent was received for the teletherapy session",
    the_session_was_conducted_via_video: "The session was conducted via video",
};

export const EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED_LABELS = {
    diagnosis_codes: "Primary diagnosis",
    secondary_diagnosis: "Secondary diagnosis",
    tertiary_diagnosis: "Tertiary diagnosis",
    additional_diagnosis_one: "Additional Diagnosis 1",
    additional_diagnosis_two: "Additional Diagnosis 2",
    files: "Files",
    interval_history: "Interval History",
    interval_psychiatric_assessment_mse:
        "Interval Psychiatric Assessment / MSE",
    patient_ability_and_capability_to_respond_to_treatment:
        "Patient's ability and capacity to respond to treatment",
    diminished_poor_or_limited_quality_patient_oriented_evidence_details:
        "If diminished/poor or limited quality patient-oriented evidence, please elaborate",
    condition_type: "Condition Type",
    condition_type_additional_details: "Condition Type Additional Details",
    condition_status: "Condition Status",
    condition_status_additional_details: "Condition Status Additional Details",
    current_medications_and_medications_changes:
        "Current Medication (s) / Medication Change (s)",
    no_side_effects_or_adverse_reactions_noted_or_reported:
        "No side effects or adverse reactions noted or reported",
    lab_tests: "Lab Tests",
    lab_tests_details: "Lab Tests Details",
    additional_documentation_review_internal:
        "Additional Documentation Review (internal)",
    additional_documentation_review_external:
        "Additional Documentation Review (external)",
    independent_interpretation_of_tests: "Independent interpretation of tests",
    discussion_of_tests_or_management_with_external_professionals:
        "Discussion of tests or management with external professionals",
    use_of_an_independent_historian:
        "Use of an independent historian (if applicable)",
    decision_regarding_hospitalization_or_patient_family_decision_to_forego_hospitalization_upon_shared_decision_making:
        "Decision regarding hospitalization, or patient/family decision to forego hospitalization upon shared decision making",
    social_factors_which_may_impair_or_prevent_patients_from_receiving_care_and_or_treatment:
        "Social factors which may impair or prevent patients from receiving care and/or treatment",
    treatment_goal_one: "Treatment goal one",
    treatment_goal_two: "Treatment goal two",
    treatment_goal_three: "Treatment goal three",
    progress_one: "Progress one",
    progress_two: "Progress two",
    progress_three: "Progress three",
    time_spent_with_patient: "Time spend with patient",
    session_location: "Session location",
    consent_was_received_for_the_teletherapy_session:
        "Consent was received for the teletherapy service",
    the_session_was_conducted_via_video: "The session was conducted via video",
    frequency_of_sessions: "Frequency of Sessions",
    other_frequency_of_sessions: "if other please specify",
    date_of_next_session: "Date of Next Session",
    location_of_the_client: "Location of the Client",
    other_location_of_the_client: "If other please specify",
    location_of_the_provider: "Location of the Provider",
    other_location_of_the_provider: "If other please specify",
    communication_factors_impacting_treatment:
        "Communication Factors impacting treatment",
    details_around_communication_factors_impacting_treatment:
        "Details around communication factors impacting treatment",
    narrative_describing_in_detail_the_issues_reviewed_with_patient:
        "Narrative describing in detail, the issues reviewed with patient",
    counseling_provided_with_patient_family_caregiver:
        "Counseling provided with patient / family / caregiver",
    type_of_therapy_performed: "Type of Therapy performed",
    recommendations_and_plan: "Recommendations and Plan",
    prognosis: "Prognosis",
    modality: "Modality",
    medication_management_duration: "Medication management duration (minutes)",
    start_time_of_counseling: "Start time of Counseling",
    end_time_of_counseling: "End time of Counseling",
};

export const EVALUATION_MANAGEMENT_NOTE_MED_LABELS = {
    diagnosis_codes: "Primary diagnosis",
    secondary_diagnosis: "Secondary diagnosis",
    tertiary_diagnosis: "Tertiary diagnosis",
    additional_diagnosis_one: "Additional Diagnosis 1",
    additional_diagnosis_two: "Additional Diagnosis 2",
    files: "Files",
    interval_history: "Interval History",
    interval_psychiatric_assessment_mse:
        "Interval Psychiatric Assessment / MSE",
    patient_ability_and_capability_to_respond_to_treatment:
        "Patient's ability and capacity to respond to treatment",
    diminished_poor_or_limited_quality_patient_oriented_evidence_details:
        "If diminished/poor or limited quality patient-oriented evidence, please elaborate",
    prognosis: "Prognosis",
    condition_type: "Condition Type",
    condition_type_additional_details: "Condition Type Additional Details",
    condition_status: "Condition Status",
    condition_status_additional_details: "Condition Status Additional Details",
    current_medications_and_medications_changes:
        "Current Medication (s) / Medication Change (s)",
    no_side_effects_or_adverse_reactions_noted_or_reported:
        "No side effects or adverse reactions noted or reported",
    lab_tests: "Lab Tests",
    lab_tests_details: "Lab Tests Details",
    additional_documentation_review_external:
        "Additional Documentation Review (external)",
    independent_interpretation_of_tests: "Independent interpretation of tests",
    discussion_of_tests_or_management_with_external_professionals:
        "Discussion of tests or management with external professionals",
    use_of_an_independent_historian:
        "Use of an independent historian (if applicable)",
    treatment_goal_one: "Treatment goal one",
    treatment_goal_two: "Treatment goal two",
    treatment_goal_three: "Treatment goal three",
    progress_one: "Progress one",
    progress_two: "Progress two",
    progress_three: "Progress three",
    session_location: "Session location",
    consent_was_received_for_the_teletherapy_session:
        "Consent was received for the teletherapy service",
    the_session_was_conducted_via_video: "The session was conducted via video",
    modality: "Modality",
    location_of_the_client: "Location of the Client",
    other_location_of_the_client: "if other please specify",
    location_of_the_provider: "Location of the Provider",
    other_location_of_the_provider: "if other please specify",
    frequency_of_sessions: "Frequency of Sessions",
    other_frequency_of_sessions: "if other please specify",
    date_of_next_session: "Date of Next Session",
    communication_factors_impacting_treatment:
        "Communication Factors impacting treatment",
    details_around_communication_factors_impacting_treatment:
        "Details around communication factors impacting treatment",
};

export const TERM_OF_CARE_NOTE_MED_LABELS = {
    diagnosis_codes: "Primary diagnosis",
    secondary_diagnosis: "Secondary diagnosis",
    tertiary_diagnosis: "Tertiary diagnosis",
    additional_diagnosis_one: "Additional Diagnosis 1",
    additional_diagnosis_two: "Additional Diagnosis 2",
    files: "Files",
    date_of_first_session: "Date of first session",
    date_of_last_session: "Date of last session",
    total_number_of_therapy_sessions:
        "Total number of therapy sessions or length of treatment",
    presenting_concerns: "Presenting Problems",
    planned_treatment_and_goals: "Planned treatment and goals",
    course_of_treatment:
        "Course of treatment (services provided, interventions, progress towards goals)",
    patient_final_condition: "Patient final condition",
    prognosis: "Prognosis",
    reason_for_termination:
        "Reason for termination (e.g. met goals, patient moved or transferred care)",
    discharge_plan_and_follow_up:
        "Discharge plan and follow-up (recommendations, referrals, post-termination contacts)",
    condition_type: "Condition Type",
    condition_type_additional_details: "Condition Type Additional Details",
    condition_status: "Condition Status",
    condition_status_additional_details: "Condition Status Additional Details",
    modality: "Modality",
    session_location: "Session location",
    consent_was_received_for_the_teletherapy_session:
        "Consent was received for the teletherapy service",
    the_session_was_conducted_via_video: "The session was conducted via video",
    location_of_the_client: "Location of the Client",
    other_location_of_the_client: "if other please specify",
    location_of_the_provider: "Location of the Provider",
    other_location_of_the_provider: "if other please specify",
};

export const TREATMENT_REVIEW_NOTE_MED_LABELS = {
    diagnosis_codes: "Primary diagnosis",
    secondary_diagnosis: "Secondary diagnosis",
    tertiary_diagnosis: "Tertiary diagnosis",
    additional_diagnosis_one: "Additional Diagnosis 1",
    additional_diagnosis_two: "Additional Diagnosis 2",
    files: "Files",
    chief_complaint: "Chief Complaint",
    current_medications: "Current Medications",
    allergies: "Allergies",
    historical_updates: "Historical updates",
    other_persons_attending_the_interview_contribution_to_the_history_or_related_observations:
        "other persons attending the interview contribution to the history or related observations",
    condition_type: "Condition Type",
    condition_type_additional_details: "Condition Type Additional Details",
    condition_status: "Condition Status",
    condition_status_additional_details: "Condition Status Additional Details",
    note: "Note",
    plan: "Plan",
    prognosis: "Prognosis",
    education_including_health_promotion_maintenance_and_psychosocial_needs:
        "Education, including health promotion, maintenance, and psychosocial needs",
    lab_work_ordered: "Lab work ordered",
    referrals_and_recommendations: "Referrals and recommendations",
    overall_goals_of_care_frequency_and_duration_of_visits:
        "overall goals of care, frequency and duration of visits",
    treatment_goal_one: "Treatment goal one",
    treatment_goal_two: "Treatment goal two",
    treatment_goal_three: "Treatment goal three",
    session_location: "Session location",
    consent_was_received_for_the_teletherapy_session:
        "Consent was received for the teletherapy service",
    the_session_was_conducted_via_video: "The session was conducted via video",
    location_of_the_client: "Location of the Client",
    other_location_of_the_client: "if other please specify",
    location_of_the_provider: "Location of the Provider",
    other_location_of_the_provider: "if other please specify",
    communication_factors_impacting_treatment:
        "Communication Factors impacting treatment",
    details_around_communication_factors_impacting_treatment:
        "Details around communication factors impacting treatment",
    appearance: "Appearance",
    movement: "Movement",
    speech: "Speech",
    affect_mood: "Affect/Mood",
    attention_concentration_cognition: "Attention/concentration/cognition",
    overall_presentation: "Overall presentation",
    patient_ability_and_capability_to_respond_to_treatment:
        "Patient's ability and capacity to respond to treatment",
    diminished_poor_or_limited_quality_patient_oriented_evidence_details:
        "If diminished/poor or limited quality patient-oriented evidence, please elaborate",
    ros: "ROS",
    current_suicidal_ideation: "Current suicidal ideation",
    current_homicidal_ideation: "Current homicidal ideation",
    reported_violent_behavior: "Reported violent behavior",
    inappropriate_illegal_behaviors: "inappropriate/illegal behaviors",
    history_of_violence_to_self: "History of violence to self",
    history_of_violence_to_others: "History of Violence to others",
    auditory_hallucinations: "Auditory hallucinations",
    visual_hallucinations: "Visual hallucinations",
    other_pertinent_information: "Other pertinent information",
    lab_test_data_review: "Lab / Test Data Review",
    psychiatric_screening_measure_results:
        "Psychiatric screening measure results",
};

export const INTAKE_NOTE_MED_LABELS = {
    diagnosis_codes: "Primary diagnosis",
    secondary_diagnosis: "Secondary diagnosis",
    tertiary_diagnosis: "Tertiary diagnosis",
    additional_diagnosis_one: "Additional Diagnosis 1",
    additional_diagnosis_two: "Additional Diagnosis 2",
    files: "Files",
    note: "Note",
    plan: "Plan",
    frequency_of_sessions: "Frequency of Sessions",
    other_frequency_of_sessions: "if other please specify",
    date_of_next_session: "Date of Next Session",
    prognosis: "Prognosis",
    education_including_health_promotion_maintenance_and_psychosocial_needs:
        "Education, including health promotion, maintenance, and psychosocial needs",
    lab_work_ordered: "Lab work ordered",
    referrals_and_recommendations: "Referrals and recommendations",
    overall_goals_of_care_frequency_and_duration_of_visits:
        "overall goals of care, frequency and duration of visits",
    treatment_goal_one: "Treatment goal one",
    treatment_goal_two: "Treatment goal two",
    treatment_goal_three: "Treatment goal three",
    session_location: "Session location",
    consent_was_received_for_the_teletherapy_session:
        "Consent was received for the teletherapy service",
    the_session_was_conducted_via_video: "The session was conducted via video",
    modality: "Modality",
    location_of_the_client: "Location of the Client",
    other_location_of_the_client: "if other please specify",
    location_of_the_provider: "Location of the Provider",
    other_location_of_the_provider: "if other please specify",
    communication_factors_impacting_treatment:
        "Communication Factors impacting treatment",
    details_around_communication_factors_impacting_treatment:
        "Details around communication factors impacting treatment",
    preferred_name: "Preferred Name",
    pronoun: "Pronoun used",
    sex: "Sex Assigned At Birth",
    gender_identity: "Gender Identity",
    sexual_orientation: "Sexual Orientation",
    monogamous_polyamorous: "Monogamous / polyamorous",
    religion: "Religion",
    religion_details: "Religion details",
    race: "Race",
    ethnicity: "Ethnicity",
    education: "Education",
    current_employment: "Current Employment",
    relationship: "Relationship",
    relationship_details: "Relationship details",
    emergency_contact_information: "Emergency Contact Information",
    names_and_relations_of_others_present_in_the_interview:
        "Names and relations of others present in the interview",
    appearance: "Appearance",
    movement: "Movement",
    speech_orientation: "Speech/ Orientation",
    affect_mood: "Affect/Mood",
    attention_concentration_cognition: "Attention/concentration/cognition",
    overall_presentation: "Overall presentation",
    patient_ability_and_capability_to_respond_to_treatment:
        "Patient's ability and capacity to respond to treatment",
    diminished_poor_or_limited_quality_patient_oriented_evidence_details:
        "If diminished/poor or limited quality patient-oriented evidence, please elaborate",
    chief_complaint: "Chief Complaint",
    history_of_present_illness: "History of present illness",
    past_psychiatric_history:
        "Past Psychiatric History (including past med trials and reactions)",
    past_medical_history: "Past Medical History",
    past_psychiatric_hospitalization: "Past psychiatric hospitalization",
    current_medications: "Current Medications",
    allergies: "Allergies",
    social_history: "Social History",
    family_history: "Family History",
    trauma_history: "Trauma History",
    lifestyle_excercise_diet_sleep: "Lifestyle: exercise, diet, sleep",
    ros: "ROS",
    current_suicidal_ideation: "Current suicidal ideation",
    current_homicidal_ideation: "Current homicidal ideation",
    reported_violent_behavior: "Reported violent behavior",
    inappropriate_illegal_behaviors: "Inappropriate/illegal behaviors",
    history_of_violence_to_self: "History of violence to self",
    history_of_violence_to_others: "History of Violence to others",
    auditory_hallucinations: "Auditory hallucinations",
    visual_hallucinations: "Visual hallucinations",
    other_pertinent_information: "Other pertinent information",
    lab_test_data_review: "Lab / Test Data Review",
    psychiatric_screening_measure_results:
        "Psychiatric screening measure results",
};

export const CLIENT_CHART_TASK_CATEGORIES = [
    TaskCategory.WRITE_SESSION_NOTE,
    TaskCategory.MAKE_REQUESTED_CHANGE,
    TaskCategory.USER_CREATED,
    TaskCategory.WRITE_FOLLOW_UP_SAFETY_NOTE,
    TaskCategory.WRITE_SUPERVISION_NOTE,
    TaskCategory.WRITE_SUPERVISOR_CHECK_IN_NOTE,
];
